import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { toast } from '../lib/toast';

const AIProcessingContext = createContext(null);

export const useAIProcessing = () => {
  const context = useContext(AIProcessingContext);
  if (!context) {
    throw new Error('useAIProcessing must be used within an AIProcessingProvider');
  }
  return context;
};

export const AIProcessingProvider = ({ children }) => {
  const { api } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ 
    current: 0, 
    total: 0, 
    items: [],
    currentPhotoItems: 0,
    totalItems: 0,
    phase: '',
    message: '',
    percentage: 0,
    // New parallel processing state
    photoStatuses: {},
    successfulPhotos: 0,
    failedPhotos: 0,
    itemsAssessed: 0,
    itemsToAssess: 0
  });
  const [results, setResults] = useState(null);
  const [tip, setTip] = useState(null);

  // Listen for logout event to clear state
  useEffect(() => {
    const handleClearAIProcessing = () => {
      setIsProcessing(false);
      setResults(null);
      setProgress({ 
        current: 0, 
        total: 0, 
        items: [],
        currentPhotoItems: 0,
        totalItems: 0,
        phase: '',
        message: '',
        percentage: 0,
        photoStatuses: {},
        successfulPhotos: 0,
        failedPhotos: 0,
        itemsAssessed: 0,
        itemsToAssess: 0
      });
      setTip(null);
    };

    window.addEventListener('clearAIProcessing', handleClearAIProcessing);
    return () => window.removeEventListener('clearAIProcessing', handleClearAIProcessing);
  }, []);

  /**
   * Process photos using the new PARALLEL endpoint
   * All photos are sent in a single request and processed concurrently
   */
  const processPhotos = useCallback(async (photos) => {
    if (photos.length === 0) {
      toast.error('Please upload at least one photo');
      return null;
    }

    if (photos.length > 5) {
      toast.error('Maximum 5 photos per batch for WANTD+ subscribers');
      return null;
    }

    setIsProcessing(true);
    setProgress({ 
      current: 0, 
      total: photos.length, 
      items: [],
      currentPhotoItems: 0,
      totalItems: 0,
      phase: 'starting',
      message: `Preparing ${photos.length} photo${photos.length > 1 ? 's' : ''}...`,
      percentage: 0,
      photoStatuses: Object.fromEntries(photos.map((_, i) => [i, 'waiting'])),
      successfulPhotos: 0,
      failedPhotos: 0,
      itemsAssessed: 0,
      itemsToAssess: 0
    });
    setResults(null);
    setTip(null);

    try {
      // Build FormData with all photos
      const formData = new FormData();
      
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        const response = await fetch(photo.preview);
        const blob = await response.blob();
        formData.append('files', blob, `photo-${i}.jpg`);
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      
      // Use the new parallel endpoint
      const url = `${backendUrl}/api/upload/analyze-images-parallel`;
      
      const allSuggestions = [];
      let lastTip = null;

      await new Promise((resolve, reject) => {
        fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }).then(async (response) => {
          if (!response.ok) {
            if (response.status === 403) {
              const errorData = await response.json().catch(() => ({}));
              setIsProcessing(false);
              toast.error('AI Photo Scanning is a WANTD+ premium feature. Upgrade to unlock!', {
                action: {
                  label: 'Upgrade',
                  onClick: () => window.location.href = '/premium'
                },
                duration: 8000
              });
              reject(new Error(errorData.detail || 'Premium feature'));
              return;
            }
            
            if (response.status === 400) {
              const errorData = await response.json().catch(() => ({}));
              setIsProcessing(false);
              toast.error(errorData.detail || 'Invalid request');
              reject(new Error(errorData.detail || 'Invalid request'));
              return;
            }
            
            const errorText = await response.text();
            reject(new Error(errorText || 'Processing failed'));
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let receivedComplete = false;
          let lastKnownProgress = null;

          const processStream = async () => {
            while (true) {
              const { done, value } = await reader.read();
              
              if (done) {
                // Stream ended - check if we received a complete event
                if (!receivedComplete) {
                  // Stream closed unexpectedly (likely server restart/timeout)
                  console.error('SSE stream closed without complete event. Last progress:', lastKnownProgress);
                  reject(new Error('Connection lost during processing. This can happen with large batches. Please try again with fewer items or retry.'));
                }
                break;
              }

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  const data = line.slice(6);
                  if (data) {
                    try {
                      const event = JSON.parse(data);
                      lastKnownProgress = event;
                      
                      if (event.error) {
                        reject(new Error(event.error));
                        return;
                      }

                      // Handle different phases
                      if (event.phase === 'starting') {
                        setProgress(prev => ({
                          ...prev,
                          phase: 'starting',
                          message: event.message || 'Starting...',
                          percentage: event.progress || 0,
                          total: event.total_photos || prev.total
                        }));
                      }
                      
                      else if (event.phase === 'detecting') {
                        setProgress(prev => ({
                          ...prev,
                          phase: 'detecting',
                          message: event.message || 'Detecting items...',
                          percentage: event.progress || 5,
                          photoStatuses: event.photo_statuses || prev.photoStatuses,
                          totalItems: event.total_items_found || prev.totalItems,
                          photoItemsCount: event.photo_items_count || prev.photoItemsCount
                        }));
                      }
                      
                      else if (event.phase === 'detection_complete') {
                        setProgress(prev => ({
                          ...prev,
                          phase: 'detection_complete',
                          message: event.message || 'Detection complete',
                          percentage: event.progress || 40,
                          totalItems: event.total_items || 0,
                          successfulPhotos: event.successful_photos || 0,
                          failedPhotos: event.failed_photos || 0,
                          photoStatuses: event.photo_statuses || prev.photoStatuses
                        }));
                      }
                      
                      else if (event.phase === 'assessing') {
                        setProgress(prev => ({
                          ...prev,
                          phase: 'assessing',
                          message: event.message || 'Analyzing items...',
                          percentage: event.progress || 45,
                          itemsToAssess: event.items_to_assess || prev.totalItems,
                          itemsAssessed: event.items_assessed || 0
                        }));
                      }
                      
                      else if (event.phase === 'complete' && event.result) {
                        receivedComplete = true;
                        const result = event.result;
                        
                        if (result.tip) {
                          lastTip = result.tip;
                        }

                        // Process all items from the result
                        if (result.items && result.items.length > 0) {
                          const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                          const itemsWithCondition = result.items.map((item, idx) => {
                            // Find the corresponding source photo
                            const sourcePhoto = photos[item.photo_index] || photos[0];
                            
                            // Construct full source_image URL if it's a relative path
                            let sourceImageUrl = item.source_image || sourcePhoto?.preview;
                            if (sourceImageUrl && sourceImageUrl.startsWith('/')) {
                              sourceImageUrl = `${backendUrl}${sourceImageUrl}`;
                            }
                            
                            return {
                              ...item,
                              condition: item.condition || 'good',
                              condition_rationale: item.condition_rationale || '',
                              estimated_rrp_new: item.estimated_rrp_new || null,
                              confidence: item.confidence === 'high' ? 95 : item.confidence === 'medium' ? 80 : 60,
                              source_photo: sourcePhoto?.preview,
                              source_image: sourceImageUrl,
                              preview_image: item.preview_image_url || item.preview_image || sourcePhoto?.preview,
                              images: item.images || [],
                              detection_box: item.detection_box || null,
                              semantic_keywords: item.semantic_keywords || []
                            };
                          });
                          allSuggestions.push(...itemsWithCondition);
                        }

                        // Update progress to complete
                        setProgress(prev => ({
                          ...prev,
                          phase: 'complete',
                          message: event.message || `Complete! ${allSuggestions.length} items ready`,
                          percentage: 100,
                          totalItems: allSuggestions.length,
                          successfulPhotos: result.photos_processed || photos.length,
                          failedPhotos: result.photos_failed || 0
                        }));

                        resolve(result);
                      }
                    } catch (parseError) {
                      console.error('Failed to parse SSE event:', parseError);
                    }
                  }
                }
              }
            }
          };

          processStream().catch(reject);
        }).catch(reject);
      });

      // Set final results
      setResults(allSuggestions);
      setTip(lastTip);
      setIsProcessing(false);

      return allSuggestions;

    } catch (error) {
      console.error('Error processing photos:', error);
      
      // If premium error, don't show additional toast
      if (error.message.includes('Premium feature')) {
        return null;
      }
      
      toast.error(`Failed to process photos: ${error.message}`);
      setIsProcessing(false);
      return null;
    }
  }, [api]);

  const clearResults = useCallback(() => {
    setResults(null);
    setProgress({ 
      current: 0, 
      total: 0, 
      items: [],
      currentPhotoItems: 0,
      totalItems: 0,
      phase: '',
      message: '',
      percentage: 0,
      photoStatuses: {},
      successfulPhotos: 0,
      failedPhotos: 0,
      itemsAssessed: 0,
      itemsToAssess: 0
    });
    setTip(null);
  }, []);

  return (
    <AIProcessingContext.Provider
      value={{
        isProcessing,
        progress,
        results,
        tip,
        processPhotos,
        clearResults,
      }}
    >
      {children}
    </AIProcessingContext.Provider>
  );
};
